// ── UNITS AGAINST THEIR GUARANTEE — the agent's read on one farm (Machine data) ──
//
// Kevin, 2026-10-31: "We should flag only if a farmers yield runs below insurance guarantee
// across their entire unit (generally county wide)." The rule, the 90%-harvested floor and
// every refusal live in src/claims/unitGuaranteeFlag.js and run server-side
// (/api/market?fn=attention&part=unit-guarantee, admin only). This file only draws the rows
// the server decided, with the sentence the server wrote. No dollars, no field-level flag.
//
// ⚠ AGENTS ONLY. JdFieldDataView renders it only when `!isPortal`, and the route itself
// refuses a non-admin; tools/check-unit-guarantee-flag.js holds both.
import React, { useEffect, useState } from "react";

const INK = "#1a2e1a", LINE = "#cdd9cd", MUTED = "#60735f", ACCENT = "#a8d878";
const ORDER = { below: 0, not_enough: 1, at_or_above: 2, not_checked: 3 };

export default function UnitGuaranteePanel({ farmerId, year, fetchJson }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!farmerId || !fetchJson) return undefined;
    let alive = true;
    setData(null); setErr(null);
    fetchJson(`/api/market?fn=attention&part=unit-guarantee&year=${encodeURIComponent(year)}&farmer=${encodeURIComponent(farmerId)}`)
      .then((d) => { if (!alive) return; if (d && d.error) setErr(String(d.error)); else setData(d || { units: [] }); })
      .catch((e) => { if (alive) setErr(String((e && e.message) || e)); });
    return () => { alive = false; };
    // fetchJson is the caller's inline wrapper (a new function every render); the read
    // depends only on the farm and the year.
  }, [farmerId, year]); // eslint-disable-line

  const box = { margin: "10px 10px 4px", border: `1px solid ${LINE}`, borderRadius: 10, background: "#fff", overflow: "hidden" };
  const head = (
    <div style={{ padding: "10px 14px", borderBottom: `1px solid ${LINE}` }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: INK }}>Units against their guarantee{year ? ` · ${year}` : ""}</div>
      <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
        Flagged only when the machine yield across the whole insurance unit is below the unit's guarantee,
        with at least {data && data.minMappedShare ? Math.round(data.minMappedShare * 100) : 90}% of the unit's acres harvested. Fields are not flagged on their own.
      </div>
    </div>
  );
  if (err) return <div style={box}>{head}<div style={{ padding: "10px 14px", fontSize: 13, color: "#b13a24" }}>Couldn't compare units with their guarantees: {err}</div></div>;
  if (!data) return <div style={box}>{head}<div style={{ padding: "10px 14px", fontSize: 13, color: MUTED }}>Reading units…</div></div>;

  const units = [...(data.units || [])].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));
  const main = units.filter((u) => u.status !== "not_checked");
  const rest = units.filter((u) => u.status === "not_checked");
  const below = units.filter((u) => u.status === "below").length;

  const row = (u) => {
    const flagged = u.status === "below";
    return (
      <div key={u.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 14px", borderTop: `1px solid ${LINE}`,
        background: flagged ? "#fdf0ec" : "#fff" }}>
        <span style={{ flexShrink: 0, marginTop: 5, width: 9, height: 9, borderRadius: 5,
          background: flagged ? "#b13a24" : u.status === "at_or_above" ? ACCENT : "#d8dfd8" }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, color: INK, fontWeight: flagged ? 800 : 600, lineHeight: 1.35 }}>{u.text}</div>
          {u.policyNumber && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>Policy {u.policyNumber}{u.plantedAcres ? ` · ${u.plantedAcres} planted ac` : ""}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={box}>
      {head}
      <div style={{ padding: "8px 14px", fontSize: 13, fontWeight: 700, color: below ? "#b13a24" : INK }}>
        {!units.length ? "No insured units with a planted crop this year."
          : below ? `${below} unit${below === 1 ? "" : "s"} below guarantee across the whole unit`
          : "No unit is below its guarantee on the machine data so far."}
      </div>
      {main.map(row)}
      {rest.length > 0 && (
        <>
          <button onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}
            style={{ display: "block", width: "100%", minHeight: 40, textAlign: "left", padding: "0 14px", border: "none", borderTop: `1px solid ${LINE}`,
              background: "#f4f8f3", color: INK, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
            {showAll ? "Hide" : "Show"} {rest.length} unit{rest.length === 1 ? "" : "s"} that can't be checked
          </button>
          {showAll && rest.map(row)}
        </>
      )}
    </div>
  );
}
